import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { AgingSlice, MonthlyPoint, plural } from './Cashflow.Dashboard.Functions';

const COLOR_ISSUED = '#bbdefb';
const COLOR_COLLECTED = '#1976d2';
const COLOR_PAID = '#e8913a';

type ChartMode = 'flow' | 'issued';

interface MonthlyChartProps {
    points: MonthlyPoint[];
    currency: string;
}

/**
 * Dos lecturas del mismo mes: qué entró contra qué salió (caja), y qué se
 * emitió a cobrar contra qué se cobró (si la cobranza acompaña a la emisión).
 */
export const MonthlyChart = ({ points, currency }: MonthlyChartProps) => {
    const [mode, setMode] = useState<ChartMode>('flow');

    const series = useMemo(() => (mode === 'flow'
        ? [
            { name: 'Cobrado', key: 'collected' as const, color: COLOR_COLLECTED },
            { name: 'Pagado', key: 'paid' as const, color: COLOR_PAID }
        ]
        : [
            { name: 'Emitido a cobrar', key: 'issued' as const, color: COLOR_ISSUED },
            { name: 'Cobrado', key: 'collected' as const, color: COLOR_COLLECTED }
        ]), [mode]);

    const option = useMemo(() => ({
        grid: { left: 8, right: 8, top: 12, bottom: 8, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            valueFormatter: (value: number) => formatCurrencyAmountWithIssue(value, currency)
        },
        xAxis: {
            type: 'category',
            data: points.map((point) => point.label),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#e0e0e0' } },
            axisLabel: { color: '#5b6675', fontFamily: 'Lato' }
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: '#eeeeee' } },
            axisLabel: {
                color: '#5b6675',
                fontFamily: 'Lato',
                formatter: (value: number) => (Math.abs(value) >= 1000 ? `${Math.round(value / 1000)} k` : `${value}`)
            }
        },
        series: series.map((item) => ({
            name: item.name,
            type: 'bar',
            data: points.map((point) => point[item.key]),
            itemStyle: { color: item.color, borderRadius: [3, 3, 0, 0] },
            barMaxWidth: 34
        }))
    }), [points, series, currency]);

    const hasData = points.some((point) => point.issued || point.collected || point.paid);

    return (
        <div className="cf-card">
            <div className="cf-card__head">
                <h3 className="cf-card__title">{mode === 'flow' ? 'Cobros y pagos por mes' : 'Emitido vs. cobrado por mes'}</h3>
                <div className="cf-chart-toggle" role="tablist" aria-label="Vista del gráfico">
                    <button type="button" role="tab" aria-selected={mode === 'flow'} className={`cf-chart-toggle__btn${mode === 'flow' ? ' is-active' : ''}`} onClick={() => setMode('flow')}>Caja</button>
                    <button type="button" role="tab" aria-selected={mode === 'issued'} className={`cf-chart-toggle__btn${mode === 'issued' ? ' is-active' : ''}`} onClick={() => setMode('issued')}>Cobranza</button>
                </div>
                <div className="cf-legend">
                    {series.map((item) => (
                        <span key={item.key} className="cf-legend__item">
                            <span className="cf-legend__swatch" style={{ background: item.color }} /> {item.name}
                        </span>
                    ))}
                </div>
            </div>
            {hasData ? (
                <ReactECharts option={option} className="cf-chart" style={{ height: 280 }} notMerge />
            ) : (
                <div className="cf-aging__empty">Sin movimientos en {currency} para el período elegido.</div>
            )}
        </div>
    );
};

interface AgingCardProps {
    slices: AgingSlice[];
    currency: string;
}

/** Cuánto de lo por cobrar está fresco y cuánto ya envejeció. */
export const AgingCard = ({ slices, currency }: AgingCardProps) => {
    const total = slices.reduce((acc, slice) => acc + slice.amount, 0);

    return (
        <div className="cf-card">
            <div className="cf-card__head">
                <h3 className="cf-card__title">Antigüedad de lo por cobrar</h3>
            </div>
            {total > 0 ? (
                <>
                    <div className="cf-aging__bar" aria-hidden>
                        {slices.map((slice) => (
                            <span key={slice.key} className={`cf-${slice.key}`} style={{ width: `${(slice.amount / total) * 100}%` }} />
                        ))}
                    </div>
                    <div className="cf-aging__rows">
                        {slices.map((slice) => (
                            <div key={slice.key} className="cf-aging__row">
                                <span className={`cf-legend__swatch cf-${slice.key}`} />
                                <span>
                                    {slice.label}
                                    {slice.count > 0 && <span className="cf-aging__count">{plural(slice.count, 'deuda', 'deudas')}</span>}
                                </span>
                                <span className="cf-num">{formatCurrencyAmountWithIssue(slice.amount, currency)}</span>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="cf-aging__empty">No hay nada pendiente de cobro en {currency}.</div>
            )}
        </div>
    );
};
